/**
 * @file Форма входа с логином и паролем.
 *
 * Компонент собирает ввод и показывает результат. signIn выполняет протокол
 * входа и проверки сессии; при уходе со страницы текущий запрос отменяется.
 */
import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { useCustomer } from '../../context/useCustomer'
import { messages } from '../../i18n/messages'
import { useLocale } from '../../i18n/useLocale'
import AuthCard from './AuthCard'
import { signIn } from './signIn'
import { type SignInErrorCode, getSignInErrorCode } from './signInError'

export default function SignInPage() {
    const { setCustomer } = useCustomer()
    const { locale } = useLocale()
    const text = messages[locale]

    // Данные формы: пароль очищается только после подтверждённого входа.
    const [login, setLogin] = useState('')
    const [password, setPassword] = useState('')

    // Отдельно храним результат и занятость, которые видит пользователь.
    const [error, setError] = useState<SignInErrorCode | null>(null)
    const [pending, setPending] = useState(false)

    // Ref защищает от двух отправок до следующего отображения компонента.
    const activeRequest = useRef<AbortController | null>(null)

    useEffect(() => () => activeRequest.current?.abort(), [])

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        if (activeRequest.current) return

        const controller = new AbortController()
        activeRequest.current = controller
        setPending(true)
        setError(null)

        try {
            const customer = await signIn(
                login,
                password,
                AbortSignal.any([
                    controller.signal,
                    AbortSignal.timeout(30_000),
                ]),
            )
            if (!controller.signal.aborted) {
                setPassword('')
                setCustomer(customer)
            }
        } catch (cause) {
            if (!controller.signal.aborted) {
                setError(getSignInErrorCode(cause))
            }
        } finally {
            activeRequest.current = null
            if (!controller.signal.aborted) setPending(false)
        }
    }

    return (
        <AuthCard title={text.title} description={text.description}>
            {/* Эта форма отвечает за ввод; signIn проверяет серверный результат до смены пользователя. */}
            <form onSubmit={handleSubmit} aria-busy={pending}>
                <label htmlFor='login'>{text.login}</label>
                <input
                    id='login'
                    name='username'
                    autoComplete='username'
                    autoCapitalize='none'
                    spellCheck={false}
                    required
                    value={login}
                    onChange={(event) => setLogin(event.target.value)}
                    disabled={pending}
                />

                <label htmlFor='password'>{text.password}</label>
                <input
                    id='password'
                    name='password'
                    type='password'
                    autoComplete='current-password'
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={pending}
                />

                {error && (
                    <p className='sign-in-error' role='alert'>
                        {text[error]}
                    </p>
                )}

                <button type='submit' disabled={pending}>
                    {pending ? text.pending : text.submit}
                </button>
            </form>
        </AuthCard>
    )
}
